import React, { useState, useRef, useEffect } from "react";
import {
  FaChevronUp,
  FaChevronDown,
  FaTimes,
  FaCheck,
  FaUndoAlt,
} from "react-icons/fa";

function _OrderRow({
  order,
  isSelected,
  onSelect,
  expandedId,
  setExpandedId,
  handleUpdateAvailability,
}) {
  const [transactions, setTransactions] = useState({});
  const [activeTab, setActiveTab] = useState("history");
  const isExpanded = expandedId === order.COI_ID;
  const contentRef = useRef(null);
  const [height, setHeight] = useState("0px");

  // ✅ Animate expand/collapse

  const formatToMMDDYYYY = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);

    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // Convert to 12-hour format

    return `${mm}/${dd}/${yyyy} ${hours}:${minutes} ${ampm}`;
  };

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isExpanded ? `${contentRef.current.scrollHeight}px` : "0px");
    }
  }, [isExpanded, transactions, activeTab]);

  // ✅ Automatically load transaction history when expanded
  useEffect(() => {
    const loadTransactionHistory = async () => {
      //console.log("order.COI_ID:", order.COI_ID);
      if (isExpanded && !transactions[order.COI_ID]) {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_BASE_URL}api/orders/get-transaction-history/${order.COI_ID}`
          );
          const data = await res.json();
          setTransactions((prev) => ({
            ...prev,
            [order.COI_ID]: data.transactions,
          }));
        } catch (error) {
          console.error("Failed to load transaction history:", error);
        }
      }
    };

    loadTransactionHistory();
  }, [isExpanded, order.COI_ID, order.CP_ID, transactions]);

  return (
    <>
      <tr>
        {/* Expand / Collapse Icon */}
        <td
          className="td-toggle-icon"
          onClick={() => setExpandedId(isExpanded ? null : order.COI_ID)}
          style={{ cursor: "pointer", width: "40px" }}
        >
          {isExpanded ? (
            <FaChevronUp className="toggle-icon" title="Collapse" />
          ) : (
            <FaChevronDown className="toggle-icon" title="Expand" />
          )}
        </td>

        {/* Checkbox */}
        <td>
          <input type="checkbox" checked={isSelected} onChange={onSelect} />
        </td>

        {/* Product Info */}
        <td>
          <img
            src={`${process.env.REACT_APP_BASE_URL}Assets/Products/${
              order.PD_ID
            }/${order.PD_ProductPicPath[0] ?? "no-image.png"}`}
            alt={order.PD_Name}
            className="ord-prod-pic"
          />
        </td>
        <td>{order.PD_Name}</td>
        <td>{order.CP_Firstname + " " + order.CP_Lastname}</td>
        <td>{order.PD_ProductType}</td>
        <td>{formatToMMDDYYYY(order.COI_Created_At)}</td>
        <td>{order.COI_Quantity}</td>
        <td>PHP {order.COI_TotalAmount * order.COI_Quantity}</td>
        <td>PHP {order.COI_Downpayment}</td>
        <td>PHP {order.COI_Balance}</td>
        <td> {order.COI_RefundReason}</td>
        <td>Refunded</td>
      </tr>

      {/* Expandable Section */}
      <tr>
        <td
          className="td-other-order-field"
          colSpan={15}
          style={{ padding: 0, border: 0 }}
        >
          <div
            ref={contentRef}
            style={{
              maxHeight: height,
              overflow: "hidden",
              transition: "max-height 0.4s ease, opacity 0.3s ease",
              opacity: isExpanded ? 1 : 0,
            }}
          >
            {/* Tab Content */}
            <div className="tab-content" style={{ padding: "10px 25px" }}>
              {/* ✅ HISTORY TAB */}
              {activeTab === "history" && (
                <div className="order-history">
                  <h4> Transaction History</h4>

                  {transactions[order.COI_ID] ? (
                    transactions[order.COI_ID].length > 0 ? (
                      transactions[order.COI_ID].map((tx, idx) =>
                        tx.Status === "refunded" ? (
                          <>
                            {" "}
                            {tx.StatusLogs?.slice() // create a shallow copy so we don’t mutate original
                              .reverse() // reverse the order
                              .map((log, i) => (
                                <>
                                  <div
                                    key={idx}
                                    style={{ marginBottom: "10px" }}
                                  >
                                    {log.action !== "refunded" ? (
                                      <p>
                                        <strong>
                                          {formatToMMDDYYYY(log.timestamp)}
                                        </strong>{" "}
                                        – PHP {tx.PaymentAmount}
                                        <br />
                                        Status:{" "}
                                        {log.action.charAt(0).toUpperCase() +
                                          log.action.slice(1)}
                                        <br />
                                        <a
                                          href={`${process.env.REACT_APP_BASE_URL}Assets/purchase_order/${tx.TransactionID}/${tx.Receipt}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "blue",
                                            cursor: "pointer",
                                          }}
                                        >
                                          <span>VIEW RECEIPT</span>
                                        </a>
                                      </p>
                                    ) : (
                                      <p>
                                        <strong>
                                          {formatToMMDDYYYY(log.timestamp)}
                                        </strong>{" "}
                                        – PHP 0
                                        <br />
                                        Status:{" "}
                                        {log.action.charAt(0).toUpperCase() +
                                          log.action.slice(1)}
                                        <br />
                                        <a
                                          href={`${process.env.REACT_APP_BASE_URL}Assets/refund_proof/${tx.TransactionID}/${tx.Proof}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "blue",
                                            cursor: "pointer",
                                          }}
                                        >
                                          <span>VIEW PROOF OF REFUND</span>
                                        </a>
                                      </p>
                                    )}
                                    <ul>
                                      {tx.OrderItems.map((item, i) => {
                                        const isHighlighted =
                                          item.ProductID === order.PD_ID ||
                                          item.ProductName === order.PD_Name;

                                        return (
                                          <li
                                            key={i}
                                            style={{
                                              backgroundColor: isHighlighted
                                                ? "gray"
                                                : "transparent",
                                              borderRadius: "5px",
                                              padding: "4px 8px",
                                              fontWeight: isHighlighted
                                                ? "600"
                                                : "normal",
                                              transition:
                                                "background-color 0.3s ease",
                                            }}
                                          >
                                            {item.ProductName} × {item.Quantity}{" "}
                                            @ PHP {item.UnitPrice} = PHP{" "}
                                            {item.TotalAmount} — Deposited: PHP{" "}
                                            {item.Deposited} Balance: PHP{" "}
                                            {item.Balance}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                </>
                              ))}
                          </>
                        ) : (
                          <div key={idx} style={{ marginBottom: "10px" }}>
                            <p>
                              <strong>
                                {formatToMMDDYYYY(tx.DatePurchase)}
                              </strong>{" "}
                              – {tx.PaymentType} via {tx.PaymentMethod} – PHP{" "}
                              {tx.PaymentAmount}
                              <br />
                              Status: {tx.Status} <br />
                              <a
                                href={`${process.env.REACT_APP_BASE_URL}Assets/purchase_order/${tx.TransactionID}/${tx.Receipt}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "blue",
                                  cursor: "pointer",
                                }}
                              >
                                <span>VIEW RECEIPT</span>
                              </a>
                            </p>

                            <ul>
                              {tx.OrderItems.map((item, i) => {
                                const isHighlighted =
                                  item.ProductID === order.PD_ID ||
                                  item.ProductName === order.PD_Name;

                                return (
                                  <li
                                    key={i}
                                    style={{
                                      backgroundColor: isHighlighted
                                        ? "gray"
                                        : "transparent",
                                      borderRadius: "5px",
                                      padding: "4px 8px",
                                      fontWeight: isHighlighted
                                        ? "600"
                                        : "normal",
                                      transition: "background-color 0.3s ease",
                                    }}
                                  >
                                    {" "}
                                    {item.ProductName} × {item.Quantity} @ PHP{" "}
                                    {item.UnitPrice} = PHP {item.TotalAmount} —
                                    Deposited: PHP {item.Deposited} Balance: PHP{" "}
                                    {item.Balance}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )
                      )
                    ) : (
                      <p>No transactions available</p>
                    )
                  ) : (
                    <p>No transactions found</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </td>
      </tr>
    </>
  );
}

export default _OrderRow;
